import * as React from 'react'
import { View, Text, ScrollView, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Tag from '@components/tag/Tag'
import ModalHeader from '@components/filter/ModalHeader'
import Drawer from '@components/drawer/Drawer'
import Button from '@components/button/Button'
import TagSection from '@components/filter/TagSection'
import { CalendarDatePickerComponent as Calendar } from '@components/picker/CalendarDatePicker'

import MetierFilter from '@components/filter/ActivityFilter'
import SpaceFilter from '@components/filter/SpaceFilter'
import ManifestationFilter from '@components/filter/ManifestationFilter'
import SocieteFilter from '@components/filter/SocieteIncidentFilter'
import UserFilter from '@components/filter/UserFilter'
import StatusInlineFilter from './StatusInlineFilter'

import useReducer from '@store/useReducer'
import * as FiltreStore from '@store/filtre/filtre'
import * as FamilleStore from '@store/famille/famille'
import * as SiteStore from '@store/site/site'

import useI18n from '@i18n/useI18n'

import { Filter } from './useFilterDisplay'

interface Props {
  filtres: FiltreState['incident']
  displayFilters: Filter[]
  places: LocalisationResponse[]
}

const GroupedFilterLayout = ({ filtres, displayFilters, places }: Props) => {
  const i18n = useI18n()

  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const societes = useReducer(FiltreStore.store, (s) => s.societesIncident)
  const users = useReducer(FiltreStore.store, (s) => s.users)

  const [sub, setSub] = React.useState<'manifestation' | 'activity' | 'location' | 'user' | 'societe' | 'date'>()
  const [local, setLocal] = React.useState(filtres)

  const localActivities = React.useMemo(
    () => activities.filter((a) => local.activity.includes(a.id)),
    [activities, local.activity]
  )

  const localSocietes = React.useMemo(
    () => societes.filter((a) => local.societe.includes(a.id)),
    [societes, local.societe]
  )

  const localPlaces = React.useMemo(() => places.filter((a) => local.location.includes(a.id)), [places, local.location])

  const localManifestations = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].filter((a) =>
        local.manifestation.includes(a.id)
      ),
    [manifestations, local.manifestation]
  )

  const localUsers = React.useMemo(() => users.filter((a) => local.user.includes(a.id)), [users, local.user])

  const onCategory = (item: 'infrastructure' | 'prestation') => {
    const isSelected = local.category.includes(item)

    setLocal({
      ...local,
      category: isSelected ? local.category.filter((id) => id !== item) : [...local.category, item],
    })
  }

  const renderSub = () => {
    if (sub === 'activity') {
      return (
        <MetierFilter
          activities={activities}
          onActivitiesSelected={(values) => setLocal({ ...local, activity: values })}
          selected={local.activity}
          style="list"
        />
      )
    }

    if (sub === 'manifestation') {
      return (
        <ManifestationFilter
          manifestations={manifestations}
          onManifestationsSelected={(values) => setLocal({ ...local, manifestation: values })}
          selected={local.manifestation}
          style="list"
        />
      )
    }

    if (sub === 'user') {
      return (
        <UserFilter
          users={users}
          onUsersSelected={(values) => setLocal({ ...local, user: values })}
          selected={local.user}
          style="list"
        />
      )
    }

    if (sub === 'societe') {
      return (
        <SocieteFilter
          societes={societes}
          onSocietesSelected={(values) => setLocal({ ...local, societe: values })}
          selected={local.societe}
          style="list"
        />
      )
    }

    if (sub === 'date') {
      return (
        <Calendar
          start={local.from ? new Date(local.from) : undefined}
          end={local.to ? new Date(local.to) : undefined}
          onDates={(start, end) =>
            setLocal({
              ...local,
              from: start?.toISOString(),
              to: end?.toISOString(),
            })
          }
          onClose={() => setSub(undefined)}
          max={new Date()}
          required={false}
        />
      )
    }

    return (
      <SpaceFilter
        places={places}
        onPlacesSelected={(values) => setLocal({ ...local, location: values })}
        selected={local.location}
        style="list"
      />
    )
  }

  const renderList = () => {
    return (
      <ScrollView contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 16 }}>
        {displayFilters.includes('category') && (
          <>
            <Title>{i18n.t('screens.incidentList.filters.tags.category')}</Title>
            <LineSection>
              <FilterContainer>
                <Tag
                  item={{
                    label: i18n.t('screens.incidentList.filters.type.infrastructure'),
                    value: local.category.includes('infrastructure'),
                  }}
                  onItemSelected={() => onCategory('infrastructure')}
                  selected={local.category.includes('infrastructure')}
                  inactiveColor
                />
              </FilterContainer>
              <FilterContainer>
                <Tag
                  item={{
                    label: i18n.t('screens.incidentList.filters.type.prestation'),
                    value: local.category.includes('prestation'),
                  }}
                  onItemSelected={() => onCategory('prestation')}
                  selected={local.category.includes('prestation')}
                  inactiveColor
                />
              </FilterContainer>
            </LineSection>
          </>
        )}

        {displayFilters.includes('manifestation') && (
          <TagSection
            title={i18n.t('screens.incidentList.filters.tags.manifestation')}
            link={{
              label: i18n.t('screens.incidentList.filters.modal.main.sections.manifestation.link'),
              onClick: () => setSub('manifestation'),
            }}
            items={localManifestations.map((m) => m.libelle)}
            onDelete={(index) =>
              setLocal({
                ...local,
                manifestation: localManifestations.filter((id, idx) => idx !== index).map((m) => m.id),
              })
            }
          />
        )}

        {displayFilters.includes('activity') && (
          <TagSection
            title={i18n.t('screens.incidentList.filters.tags.activity')}
            link={{
              label: i18n.t('screens.incidentList.filters.modal.main.sections.job.link'),
              onClick: () => setSub('activity'),
            }}
            items={localActivities.map((a) => a.libelle)}
            onDelete={(index) =>
              setLocal({ ...local, activity: localActivities.filter((id, idx) => idx !== index).map((a) => a.id) })
            }
          />
        )}

        {displayFilters.includes('societe') && (
          <TagSection
            title={i18n.t('screens.incidentList.filters.tags.societe')}
            link={{
              label: i18n.t('screens.incidentList.filters.modal.main.sections.societe.link'),
              onClick: () => setSub('societe'),
            }}
            items={localSocietes.map((a) => a.libelle)}
            onDelete={(index) =>
              setLocal({ ...local, societe: localSocietes.filter((id, idx) => idx !== index).map((a) => a.id) })
            }
          />
        )}

        {displayFilters.includes('location') && (
          <TagSection
            title={i18n.t('screens.incidentList.filters.tags.location')}
            link={{
              label: i18n.t('screens.incidentList.filters.modal.main.sections.location.link'),
              onClick: () => setSub('location'),
            }}
            items={localPlaces.map((p) => p.libelle)}
            onDelete={(index) =>
              setLocal({ ...local, location: localPlaces.filter((id, idx) => idx !== index).map((p) => p.id) })
            }
          />
        )}

        {displayFilters.includes('user') && (
          <TagSection
            title={i18n.t('screens.incidentList.filters.tags.user')}
            link={{
              label: i18n.t('screens.incidentList.filters.modal.main.sections.user.link'),
              onClick: () => setSub('user'),
            }}
            items={localUsers.map((p) => `${p.nom} ${p.prenom}`)}
            onDelete={(index) =>
              setLocal({ ...local, user: localUsers.filter((id, idx) => idx !== index).map((p) => p.id) })
            }
          />
        )}

        {(displayFilters.includes('created') ||
          displayFilters.includes('followed') ||
          displayFilters.includes('new') ||
          displayFilters.includes('prioritaire')) && (
          <>
            <Title>{i18n.t('screens.incidentList.filters.modal.main.sections.other')}</Title>
            <LineSection>
              {displayFilters.includes('created') && (
                <FilterContainer>
                  <Tag
                    item={{
                      label: i18n.t('screens.incidentList.filters.tags.created'),
                      value: local.created,
                    }}
                    onItemSelected={() => setLocal({ ...local, created: !local.created })}
                    selected={local.created}
                    inactiveColor
                  />
                </FilterContainer>
              )}

              {displayFilters.includes('followed') && (
                <FilterContainer>
                  <Tag
                    item={{
                      label: i18n.t('screens.incidentList.filters.tags.followed'),
                      value: local.followed,
                    }}
                    onItemSelected={() => setLocal({ ...local, followed: !local.followed })}
                    selected={local.followed}
                    inactiveColor
                  />
                </FilterContainer>
              )}

              {displayFilters.includes('new') && (
                <FilterContainer>
                  <Tag
                    item={{
                      label: i18n.t('screens.incidentList.filters.tags.isNew'),
                      value: local.isNew,
                    }}
                    onItemSelected={() => setLocal({ ...local, isNew: !local.isNew })}
                    selected={local.isNew}
                    inactiveColor
                  />
                </FilterContainer>
              )}

              {displayFilters.includes('prioritaire') && (
                <FilterContainer>
                  <Tag
                    item={{
                      label: i18n.t('screens.incidentList.filters.tags.prioritaire'),
                      value: local.prioritaire,
                    }}
                    onItemSelected={() => setLocal({ ...local, prioritaire: !local.prioritaire })}
                    selected={local.prioritaire}
                    inactiveColor
                  />
                </FilterContainer>
              )}
            </LineSection>
          </>
        )}

        {/* DATES */}
        {displayFilters.includes('from') && displayFilters.includes('to') && (
          <>
            <Title>{i18n.t('screens.incidentList.filters.tags.date')}</Title>
            <Line activeOpacity={0.8} onPress={() => setSub('date')}>
              <DateInfo>{i18n.t('components.datePicker.from')}</DateInfo>
              <DateContainer>
                <DateInfo>
                  {local.from
                    ? i18n.t('components.datePicker.date', { date: new Date(local.from) })
                    : i18n.t('components.datePicker.empty')}
                </DateInfo>
              </DateContainer>
              <DateInfo>{i18n.t('components.datePicker.to')}</DateInfo>
              <DateContainer>
                <DateInfo>
                  {local.to
                    ? i18n.t('components.datePicker.date', { date: new Date(local.to) })
                    : i18n.t('components.datePicker.empty')}
                </DateInfo>
              </DateContainer>
            </Line>
          </>
        )}

        {displayFilters.includes('status') && (
          <>
            <Title>{i18n.t('screens.incidentList.filters.tags.status')}</Title>
            <StatusInlineFilter selected={local.status} onStatusSelected={(status) => setLocal({ ...local, status })} />
          </>
        )}
      </ScrollView>
    )
  }

  if (sub === 'date') {
    return renderSub()
  }

  if (!!sub) {
    return (
      <>
        <DrawerHeader>
          <ModalHeader
            left={{ name: 'arrow-left', onClick: () => setSub(undefined) }}
            title={i18n.t(`screens.incidentList.filters.tags.${sub}`)}
            right={{
              label: i18n.t('common.erase'),
              onClick: () => setLocal({ ...local, [sub]: FiltreStore.emptyFilters[sub] }),
            }}
          />
        </DrawerHeader>

        <Full>{renderSub()}</Full>

        <ButtonContainer>
          <Button
            label={i18n.t('screens.incidentList.validateFilter')}
            onClick={() => setSub(undefined)}
            status="active"
          />
        </ButtonContainer>
      </>
    )
  }

  return (
    <>
      <DrawerHeader>
        <ModalHeader
          left={{ name: 'close', onClick: Drawer.close }}
          title={i18n.t('screens.incidentList.filters.modal.main.title')}
          right={{
            label: i18n.t('screens.incidentList.filters.modal.main.reset'),
            onClick: () => setLocal(FiltreStore.emptyFilters),
          }}
        />
      </DrawerHeader>

      <Full>{renderList()}</Full>

      <ButtonContainer>
        <Button
          label={i18n.t('screens.incidentList.filters.modal.button')}
          onClick={() => {
            FiltreStore.actions.setIncidentFilter(local)
            Drawer.close()
          }}
          status="active"
        />
      </ButtonContainer>
    </>
  )
}

export default GroupedFilterLayout

const DrawerHeader = styled(View)`
  margin: 0 16px;
  margin-top: 30px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
`

const Full = styled(View)`
  flex: 1;
`

const ButtonContainer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const LineSection = styled(View)`
  flex-flow: row wrap;
  margin-bottom: 24px;
`

const FilterContainer = styled(View)`
  margin: 8px 0;
  margin-right: 8px;
`

const Line = styled(TouchableOpacity)`
  flex-direction: row;
  margin-bottom: 24px;
  align-items: center;
`

const DateInfo = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
`

const DateContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.backgroundTertiary};
  border-radius: 8px;
  padding: 8px 16px;
  margin: 0 8px;
`
